import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type { BeneficiaryFetchFailedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryFetchFailed';
import FetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/Queries/FetchBeneficiary';
import fetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/fetchBeneficiary';
import { PermissionEnum } from '@Application/Security/Roles';
import ErrorState from '@Guidelines/Component/ErrorState';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { reveal } from '@Guidelines/Theme/helpers/animations';
import BeneficiaryIdentityHeader from '@UI/Pages/Beneficiaries/BeneficiaryDetails/BeneficiaryIdentityHeader/BeneficiaryIdentityHeader';
import BeneficiaryDangerZone from '@UI/Pages/Beneficiaries/BeneficiaryDetails/BeneficiarySummaryAside/BeneficiaryDangerZone';
import BeneficiarySummaryAside from '@UI/Pages/Beneficiaries/BeneficiaryDetails/BeneficiarySummaryAside/BeneficiarySummaryAside';
import SubjectActionsRow from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectActionsRow';
import SubjectDetailsSkeleton from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectDetailsSkeleton';
import SubjectReadingColumn from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectReadingColumn';
import PendingRecordingsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/PendingRecordingsSection';
import CompletedPreparationsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/CompletedPreparationsSection';
import OpenPreparationsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/OpenPreparationsSection';
import PendingDocumentsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/PendingDocumentsSection';
import useFacilityUsers from '@UI/Pages/Beneficiaries/Hooks/useFacilityUsers';
import PreparationsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/PreparationsNotifications';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type FacilityUserView from '@UI/Store/FacilityUsers/Views/FacilityUserView';
import { FilePlus2Icon, FileUserIcon } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

/**
 * Detects a `BENEFICIARY_FETCH_FAILED` rejection carrying a `404`: the only status this screen reacts
 * to by leaving the record entirely. `dispatch(...)`'s rejection is the failed action itself
 * (`Application/Beneficiaries/fetchBeneficiary.ts` rejects with it) — `useStoreQuery` would otherwise
 * re-wrap it into a generic `Error`, losing the status carried by `ExtractedMessages`.
 */
const isBeneficiaryNotFoundFailure = (candidate: unknown): candidate is BeneficiaryFetchFailedAction => {
	if (typeof candidate !== 'object' || candidate === null) {
		return false;
	}

	const action = candidate as Partial<BeneficiaryFetchFailedAction>;

	return action.type === 'BENEFICIARY_FETCH_FAILED' && action.payload?.status === 404;
};

type Props = {
	subject: SubjectAddress;
};

/**
 * Owns the dossier fetch and every loading/error/not-found/nominal branch of the details screen, once
 * the beneficiary id is known valid.
 *
 * The dossier has no tabs: one reading column stacks the identity header, the actions ("Commencer des
 * documents", and the toggle that reveals the summary aside) and autonomous `Sections/*` — the
 * preparations in progress, the latest documents to verify (with the way to the filtered list), the
 * completed preparations, and, only when the device holds recordings no preparation in progress accounts
 * for, an "Enregistrements en attente sur cet appareil" notice. Each section loads what it draws; a
 * future change to the inside of one section never moves this file.
 *
 * The dossier carries only its referents' ids (`assignedUserIds`), never their names, so the "Suivi
 * par…" line (`BeneficiaryIdentityHeader`) resolves them against the agency directory
 * (`useFacilityUsers`). That line falls back to the count whole as soon as one id cannot be resolved,
 * rather than naming a subset that would read as the full set. `BeneficiarySummaryAside` resolves the
 * same ids on its own, from the same cached directory.
 *
 * The branch order is deliberate. `notFound` renders `null` first: a `404` on the dossier fetch sets
 * both `notFound` and `error` (the rejection is rethrown for `useStoreQuery` to capture), and this
 * screen is already leaving for the beneficiary list — reading `error` first would flash the error state for
 * the one render before the redirect takes effect. Past that, `error !== null` is checked before the
 * data-absent branch, never the reverse, so an absent `beneficiary` is never read as a failure
 * (`guides/state-stores.md`, "Ce que `useStoreQuery` ne garantit pas").
 */
const BeneficiaryDetailsContent: React.FC<Props> = ({ subject }) => {
	const beneficiaryId = subject.subjectId;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const [notFound, setNotFound] = React.useState(false);
	const [isInfoOpen, setIsInfoOpen] = React.useState(false);
	const infoPanelId = React.useId();

	const {
		data: beneficiary,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.beneficiariesStore.beneficiary,
		stopOn: (value) => value !== null && value.id === beneficiaryId,
		query: () =>
			dispatch(fetchBeneficiary(new FetchBeneficiary(facilityId, beneficiaryId))).catch(
				(rejection: unknown) => {
					if (isBeneficiaryNotFoundFailure(rejection)) {
						setNotFound(true);
					}

					throw rejection;
				},
			),
		// `facilityId` is not part of the View's own scope (it is not compared by `stopOn`), but it must
		// still sit in `deps`: switching agency in the header selector changes it mid-session, and this
		// dossier must refetch under the new agency rather than keep showing the previous one's data.
		deps: [beneficiaryId, facilityId],
	});

	const { data: facilityUsers } = useFacilityUsers();

	React.useEffect(() => {
		if (notFound) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
		}
	}, [notFound, navigate]);

	const followedByNames = React.useMemo(() => {
		if (beneficiary === null || facilityUsers === null) {
			return [];
		}

		return beneficiary.assignedUserIds
			.map((userId) => facilityUsers.list.find((user) => user.id === userId))
			.filter((user): user is FacilityUserView => user !== undefined)
			.map((user) => user.fullName);
	}, [beneficiary, facilityUsers]);

	const assignedUserCount = beneficiary !== null ? beneficiary.assignedUserIds.length : 0;

	// Feeds the "Suivi par…" line. A referent absent from the directory (account removed from the
	// agency) still counts, so the line falls back to the count whole rather than naming a subset that
	// would read as everyone.
	const assignedUsersSummaryText = React.useMemo(() => {
		if (assignedUserCount === 0) {
			return null;
		}

		return followedByNames.length === assignedUserCount
			? followedByNames.join(', ')
			: `${assignedUserCount} référent(s) affecté(s)`;
	}, [assignedUserCount, followedByNames]);

	const handleToggleInfo = React.useCallback(() => {
		setIsInfoOpen((current) => !current);
	}, []);

	const handleStartDocumentsClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/preparations/new`);
	}, [subject, navigate]);

	if (notFound) {
		return null;
	}

	if (error !== null) {
		return <ErrorState title="Impossible de charger le dossier" onRetry={refetch} />;
	}

	if (beneficiary === null) {
		return <SubjectDetailsSkeleton />;
	}

	return (
		<SubjectReadingColumn>
			<BeneficiaryIdentityHeader
				beneficiary={beneficiary}
				assignedUsersSummaryText={assignedUsersSummaryText}
			>
				<SubjectActionsRow>
					<ConditionalLoader data={!beneficiary.isArchived}>
						<WithPermission
							permission={[
								PermissionEnum.INTERVIEW_WRITE,
								PermissionEnum.DOCUMENT_GENERATION_LAUNCH,
							]}
							mode="any"
						>
							<Button
								type="button"
								size="lg"
								icon={<FilePlus2Icon size={16} />}
								label="Commencer des documents"
								onClick={handleStartDocumentsClick}
							/>
						</WithPermission>
					</ConditionalLoader>
					<Button
						type="button"
						severity="help"
						icon={<FileUserIcon size={16} />}
						aria-expanded={isInfoOpen}
						aria-controls={infoPanelId}
						label={isInfoOpen ? 'Masquer les informations' : 'Informations du bénéficiaire'}
						onClick={handleToggleInfo}
					/>
				</SubjectActionsRow>
			</BeneficiaryIdentityHeader>

			<ConditionalLoader data={isInfoOpen}>
				<div id={infoPanelId} css={reveal(0)}>
					<BeneficiarySummaryAside beneficiary={beneficiary} />
				</div>
			</ConditionalLoader>

			<OpenPreparationsSection subject={subject} isArchived={beneficiary.isArchived} />
			<PendingDocumentsSection subject={subject} />
			<CompletedPreparationsSection subject={subject} />
			<ConditionalLoader data={!beneficiary.isArchived}>
				<PendingRecordingsSection subject={subject} />
			</ConditionalLoader>
			<BeneficiaryDangerZone beneficiaryId={beneficiary.id} isArchived={beneficiary.isArchived} />
			<PreparationsNotifications />
		</SubjectReadingColumn>
	);
};

export default BeneficiaryDetailsContent;
